import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import countriesAndTimezones from 'countries-and-timezones';
import { UsaStates } from 'usa-states';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import { Field, reduxForm, SubmissionError } from 'redux-form';

import Button from 'components/common/Button';
import TextField from 'components/fields/TextField';
import AutosuggestField from 'components/fields/AutosuggestField';
import FormError from 'components/common/FormError';

import { updateBusiness } from 'redux/modules/business';
import { requestFetchPlacesAutocomplete } from 'redux/modules/placesAutocomplete';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';
import objectDiff from 'utils/objectDiff';

const usaStates = new UsaStates();
const usaTimeZones = countriesAndTimezones.getTimezonesForCountry('US')
  .map(item => item.name);

const styles = theme => ({
  grid: {
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
  },
  gridGutter: {
    marginTop: 15,
  },
  form: {
    margin: '10px 40px',
    minHeight: 240,
  },
  typography: {
    paddingBottom: 2,
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
  },
});

function onSubmit(values, dispatch, props) {
  const diffValues = props.initialValues.ID ? objectDiff(values, props.initialValues) : {};
  return new Promise((resolve, reject) => {
    dispatch(updateBusiness({
      values: {
        'Company Address': props.initialValues.ID ? {
          ID: props.initialValues.ID,
          ...diffValues,
        } : values,
      },
      resolve,
      reject,
      showNotification: false,
    }));
  }).then(res => res,
    (err) => {
      if (err.error) {
        throw new SubmissionError({ _error: err.error });
      }
      throw new SubmissionError(err);
    });
}

const BusinessGeneralCompanyAddressForm = ({ handleSubmit, change, error, classes }) => (
  <form
    className={classes.form}
    noValidate
    onSubmit={handleSubmit}
    autoComplete="off"
  >
    {error &&
      <Grid
        className={classes.grid}
        container
        spacing={24}
      >
        <Grid
          item
          xs={12}
        >
          <FormError>
            {error}
          </FormError>
        </Grid>
      </Grid>
    }
    <Grid
      className={classNames(classes.grid, classes.gridGutter)}
      container
      spacing={24}
    >
      <Grid item xs={12}>
        <Typography className={classes.typography} align="center">
          Enter your Contact Info and these will populate on forms such as Delivery Tickets and Letters of Medical Necessity
        </Typography>
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid item xs={8}>
        <Field
          id="Street"
          name="Street"
          label="Street *"
          component={AutosuggestField}
          validate={[validate.required]}
          placeholder="Lookup Address"
          fullWidth
          renderSuggestionContent={(suggestion) => suggestion.address.label}
          onSuggestionSelected={(suggestion) => {
            if (suggestion.address.city) {
              change('City', suggestion.address.city);
            }
            if (suggestion.address.postalCode) {
              change('Zip Code', suggestion.address.postalCode);
            }
            if (suggestion.address.stateCode) {
              change('State', suggestion.address.stateCode);
            }
            if (suggestion.address.street) {
              if (suggestion.address.houseNumber) {
                return `${suggestion.address.houseNumber} ${suggestion.address.street}`;
              }
              return suggestion.address.street;
            }
            return suggestion.address.label;
          }}
          fetchSuggestions={({ value }) => (
            value.length > 0
              ? requestFetchPlacesAutocomplete({
                q: value,
                in: 'countryCode:USA',
                limit: 5,
              }).then(res => res.items,
                err => err)
              : new Promise(resolve => resolve([]))
          )}
        />
      </Grid>
      <Grid item xs={3}>
        <Field
          id="Apt"
          name="Apt"
          label="Suite (Optional)"
          component={TextField}
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid item xs={5}>
        <Field
          id="City"
          name="City"
          label="City *"
          component={TextField}
          validate={[validate.required]}
          fullWidth
        />
      </Grid>
      <Grid item xs={3}>
        <Field
          id="State"
          name="State"
          label="State *"
          component={AutosuggestField}
          normalize={normalize.uppercase}
          validate={[validate.required]}
          fullWidth
          renderSuggestionContent={suggestion => suggestion.abbreviation}
          onSuggestionSelected={suggestion => suggestion.abbreviation}
          fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
            if (offset === 0) {
              if (value.length > 0) {
                resolve(usaStates.states.filter(item =>
                  item.abbreviation.indexOf(value) === 0));
              } else {
                resolve(usaStates.states);
              }
            } else {
              resolve([]);
            }
          }))}
          // suggestionsPlacement="top"
        />
      </Grid>
      <Grid item xs={3}>
        <Field
          id="Zip Code"
          name="Zip Code"
          label="Zip Code *"
          component={TextField}
          validate={[validate.required]}
          fullWidth
        />
      </Grid>
    </Grid>
  </form>
);

BusinessGeneralCompanyAddressForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  error: PropTypes.string,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
    form: PropTypes.string.isRequired,
    typography: PropTypes.string.isRequired,
  }).isRequired,
};

BusinessGeneralCompanyAddressForm.defaultProps = {
  error: null,
};

export default reduxForm({
  form: 'companyAddress',
  enableReinitialize: true,
  onSubmit,
})(withStyles(styles)(BusinessGeneralCompanyAddressForm));



// WEBPACK FOOTER //
// ./src/components/settings/BusinessGeneralDialog/BusinessGeneralCompanyAddressForm.js